<template>
  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="sales"
        :row-key="'id'"
        :loading="loading"
        :pagination="paginationConfig"
        :scroll="{ x: 1400 }"
        class="text-nowrap align-middle sales-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ t("sales_page.no_sales_found") }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'date'">
            {{ formatDate(record.created_at || record.date) }}
          </template>

          <template v-else-if="column && column.key === 'customer'">
            {{ record.customer_name || record.customer?.name || t("sales_page.walk_in_customer") }}
          </template>

          <template v-else-if="column && column.key === 'warehouse'">
            {{ record.warehouse_name || record.warehouse?.name || t("sales_page.not_available_short") }}
          </template>

          <template v-else-if="column && column.key === 'status'">
            <span class="status-badge" :class="`status-${String(record.status || 'pending').toLowerCase()}`">
              {{ formatStatus(record.status) }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'grand_total'">
            {{ formatCurrency(record.grand_total) }}
          </template>

          <template v-else-if="column && column.key === 'paid'">
            {{ formatCurrency(record.paid) }}
          </template>

          <template v-else-if="column && column.key === 'due'">
            {{ formatCurrency(record.due ?? Number(record.grand_total || 0) - Number(record.paid || 0)) }}
          </template>

          <template v-else-if="column && column.key === 'payment_status'">
            <span class="status-badge" :class="`payment-${String(record.payment_status || 'paid').toLowerCase()}`">
              {{ formatStatus(record.payment_status || "paid") }}
            </span>
          </template>

          <template v-else-if="column && column.key === 'paid_by'">
            {{ record.paid_by || t("sales_page.cash") }}
          </template>

          <template v-else-if="column && column.key === 'actions'">
            <div class="d-flex justify-content-center gap-2 table-actions">
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('sales_page.view')"
                data-bs-toggle="modal"
                data-bs-target="#detailsModal"
                @click="$emit('view-sale', record)"
              >
                <img src="../../../assets/img/icons/eye.svg" :alt="t('sales_page.view')" style="width: 14px" />
              </button>
              <router-link
                v-if="showEdit"
                :to="`${editRouteBase}/${record.id}`"
                :title="t('sales_page.edit')"
                class="btn p-0 border-0 action-icon-btn"
              >
                <img src="../../../assets/img/icons/edit.svg" :alt="t('sales_page.edit')" style="width: 14px" />
              </router-link>
              <button
                class="btn p-0 border-0 action-icon-btn"
                :title="t('sales_page.delete')"
                @click="$emit('delete-sale', record)"
              >
                <img src="../../../assets/img/icons/close.svg" :alt="t('sales_page.delete')" style="width: 14px" />
              </button>
            </div>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { Sale } from "../../../services/sales";

export default defineComponent({
  name: "SalesList",
  props: {
    sales: {
      type: Array as () => Sale[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showEdit: {
      type: Boolean,
      default: true,
    },
    editRouteBase: {
      type: String,
      default: "/edit-sales",
    },
  },
  emits: ["view-sale", "delete-sale"],
  setup() {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t("sales_page.date"), dataIndex: "created_at", key: "date", align: "center" as const, width: 210 },
      { title: t("sales_page.reference"), dataIndex: "reference_no", key: "reference_no", align: "center" as const, width: 150 },
      { title: t("sales_page.customer"), key: "customer", align: "center" as const, width: 190 },
      { title: t("sales_page.warehouse"), key: "warehouse", align: "center" as const, width: 180 },
      { title: t("sales_page.status"), key: "status", align: "center" as const, width: 140 },
      { title: t("sales_page.grand_total"), key: "grand_total", align: "center" as const, width: 150 },
      { title: t("sales_page.paid"), key: "paid", align: "center" as const, width: 140 },
      { title: t("sales_page.due"), key: "due", align: "center" as const, width: 140 },
      { title: t("sales_page.payment_status"), key: "payment_status", align: "center" as const, width: 160 },
      { title: t("sales_page.paid_by"), key: "paid_by", align: "center" as const, width: 160 },
      { title: t("sales_page.action"), key: "actions", align: "center" as const, width: 120 },
    ]);

    const paginationConfig = computed(() => ({
      showSizeChanger: true,
      pageSizeOptions: ["10", "20", "30", "50", "100"],
      defaultPageSize: 10,
      locale: { items_per_page: t("sales_page.items_per_page") },
    }));

    const formatDate = (dateStr: string) => {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleString(locale.value === "km" ? "km-KH" : "en-GB", {
        timeZone: "Asia/Phnom_Penh",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    };

    const formatStatus = (status?: string) => {
      const value = String(status || "pending").toLowerCase();
      const statusKeyMap: Record<string, string> = {
        pending: "sales_page.status_pending",
        sent: "sales_page.status_sent",
        completed: "sales_page.status_completed",
        paid: "sales_page.status_paid",
        partial: "sales_page.status_partial",
        due: "sales_page.status_due",
        unpaid: "sales_page.status_unpaid",
      };

      return statusKeyMap[value] ? t(statusKeyMap[value]) : value.charAt(0).toUpperCase() + value.slice(1);
    };

    const formatCurrency = (value?: number) => `$${Number(value || 0).toFixed(2)}`;

    return {
      t,
      columns,
      paginationConfig,
      formatDate,
      formatStatus,
      formatCurrency,
    };
  },
});
</script>

<style scoped>
.sales-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.sales-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}

.table-actions {
  min-width: 72px;
}

.action-icon-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.status-completed {
  color: #1f9254;
  background: #e8f7ee;
}

.status-pending {
  color: #d33c43;
  background: #fdecec;
}

.status-sent {
  color: #3563e9;
  background: #edf4ff;
}

.payment-paid {
  color: #1f9254;
  background: #e8f7ee;
}

.payment-partial {
  color: #b26a00;
  background: #fff4d6;
}

.payment-pending,
.payment-due,
.payment-unpaid {
  color: #d33c43;
  background: #fdecec;
}
</style>
